/**
 * Blocking API — Firestore CRUD for Blocking Paths & Choreography Sequences
 * 
 * Storage pattern: Subcollections under the project document
 *   organizations/{orgId}/projects/{projectId}/blocking/{pathId}           → BlockingPath
 *   organizations/{orgId}/projects/{projectId}/choreography/{sequenceId}   → ChoreographySequence
 * 
 * Migrated from legacy data-document arrays (projects/{id}/data/blocking).
 * This follows the same pattern as schedule-api.ts.
 */

import {
    collection,
    onSnapshot,
    Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/firebase-config';
import { BlockingPath, ChoreographySequence } from '../blocking/types';
import { logger } from '@/utils/logger';
import { buildProjectSubcollectionPath, type ProjectScope } from './project-scope';
import { enqueueStructuredEntityWrite, type StructuredEntityWriteRegistration } from './structured-entity-write';

const BLOCKING_SUBCOLLECTION = 'blocking';
const CHOREOGRAPHY_SUBCOLLECTION = 'choreography';

// ---------------------
// Helpers
// ---------------------

function blockingCol(scope: ProjectScope) {
    return collection(db, buildProjectSubcollectionPath(scope, BLOCKING_SUBCOLLECTION));
}

function choreographyCol(scope: ProjectScope) {
    return collection(db, buildProjectSubcollectionPath(scope, CHOREOGRAPHY_SUBCOLLECTION));
}

const BLOCKING_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'blocking_choreography',
    capability: 'EDIT_BLOCKING',
    conflictPrefix: BLOCKING_SUBCOLLECTION,
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: 'Authentication required to preserve blocking/choreography project writes',
};

const CHOREOGRAPHY_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'blocking_choreography',
    capability: 'EDIT_BLOCKING',
    conflictPrefix: CHOREOGRAPHY_SUBCOLLECTION,
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: 'Authentication required to preserve blocking/choreography project writes',
};

// ---------------------
// Blocking Path CRUD
// ---------------------

/**
 * Subscribes to blocking paths for a project.
 */
export function subscribeToBlocking(
    scope: ProjectScope,
    onUpdate: (paths: BlockingPath[]) => void
): Unsubscribe {
    return onSnapshot(blockingCol(scope), (snap) => {
        const paths = snap.docs.map(d => ({ ...d.data(), id: d.id } as BlockingPath));
        onUpdate(paths);
    }, (error) => {
        logger.error('[blocking-api] Blocking subscription error:', error);
    });
}

/**
 * Saves (creates or updates) a single blocking path.
 */
export async function saveBlockingPath(scope: ProjectScope, path: BlockingPath): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: BLOCKING_WRITE_REGISTRATION,
        entityId: path.id,
        kind: 'patchEntity',
        payload: path,
    });
}

/**
 * Deletes a blocking path.
 */
export async function deleteBlockingPath(scope: ProjectScope, pathId: string): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: BLOCKING_WRITE_REGISTRATION,
        entityId: pathId,
        kind: 'deleteEntity',
        payload: { id: pathId },
    });
}

/**
 * Batch-saves multiple blocking paths.
 */
export async function batchSaveBlockingPaths(scope: ProjectScope, paths: BlockingPath[]): Promise<void> {
    await Promise.all(paths.map((path) => saveBlockingPath(scope, path)));
}

// ---------------------
// Choreography Sequence CRUD
// ---------------------

/**
 * Subscribes to choreography sequences for a project.
 */
export function subscribeToChoreography(
    scope: ProjectScope,
    onUpdate: (sequences: ChoreographySequence[]) => void
): Unsubscribe {
    return onSnapshot(choreographyCol(scope), (snap) => {
        const sequences = snap.docs.map(d => ({ ...d.data(), id: d.id } as ChoreographySequence));
        onUpdate(sequences);
    }, (error) => {
        logger.error('[blocking-api] Choreography subscription error:', error);
    });
}

/**
 * Saves (creates or updates) a single choreography sequence.
 */
export async function saveChoreographySequence(scope: ProjectScope, seq: ChoreographySequence): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: CHOREOGRAPHY_WRITE_REGISTRATION,
        entityId: seq.id,
        kind: 'patchEntity',
        payload: seq,
    });
}

/**
 * Deletes a choreography sequence.
 */
export async function deleteChoreographySequence(scope: ProjectScope, seqId: string): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: CHOREOGRAPHY_WRITE_REGISTRATION,
        entityId: seqId,
        kind: 'deleteEntity',
        payload: { id: seqId },
    });
}
